import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

export default function CSS_ObjectFit() {
  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">📐 Advanced CSS: Object Fit</h1>

      <p className="tutorial-intro">
        The CSS <code>object-fit</code> property defines how an{" "}
        <code>&lt;img&gt;</code> or <code>&lt;video&gt;</code> fills its
        container. Combined with <code>object-position</code>, you can control
        placement and scaling of media.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#075985" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            <code>fill</code> stretches to fit container, may distort.
          </li>
          <li>
            <code>contain</code> fits inside while keeping aspect ratio.
          </li>
          <li>
            <code>cover</code> fills container while keeping aspect ratio, may
            crop.
          </li>
          <li>
            <code>none</code> keeps original image size.
          </li>
          <li>
            <code>scale-down</code> scales down if image is larger than
            container.
          </li>
          <li>
            Use <code>object-position</code> to align image inside container.
          </li>
        </ul>
      </div>

      {/* Examples */}
      <div className="tutorial-section">
        <h2 className="section-title">✨ Object-fit Examples</h2>

        {/* Fill */}
        <h3>1. Fill (stretches, may distort)</h3>
        <CodeCopyBlock
          code={`img { width: 300px; height: 150px; object-fit: fill; }`}
        />
        <img
          src="https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=500&q=80"
          alt="fill"
          style={{
            width: "300px",
            height: "150px",
            objectFit: "fill",
            borderRadius: "12px",
            marginBottom: "1rem",
          }}
        />

        {/* Contain */}
        <h3>2. Contain (fits inside, keeps aspect ratio)</h3>
        <CodeCopyBlock
          code={`img { width: 300px; height: 150px; object-fit: contain; background: #e0f2fe; }`}
        />
        <img
          src="https://images.unsplash.com/photo-1518791841217-8f162f1e1131?auto=format&fit=crop&w=800&q=80"
          alt="contain"
          style={{
            width: "300px",
            height: "150px",
            objectFit: "contain",
            backgroundColor: "#e0f2fe",
            borderRadius: "12px",
            marginBottom: "1rem",
          }}
        />

        {/* Cover */}
        <h3>3. Cover (fills container, may crop)</h3>
        <CodeCopyBlock
          code={`img { width: 300px; height: 150px; object-fit: cover; }`}
        />
        <img
          src="https://images.unsplash.com/photo-1526772662000-3f88f10405ff?auto=format&fit=crop&w=600&q=80"
          alt="cover"
          style={{
            width: "300px",
            height: "150px",
            objectFit: "cover",
            borderRadius: "12px",
            marginBottom: "1rem",
          }}
        />

        {/* None */}
        <h3>4. None (original size)</h3>
        <CodeCopyBlock
          code={`img { width: 300px; height: 150px; object-fit: none; }`}
        />
        <img
          src="https://images.unsplash.com/photo-1519125323398-675f0ddb6308?auto=format&fit=crop&w=800&q=80"
          alt="none"
          style={{
            width: "300px",
            height: "150px",
            objectFit: "none",
            borderRadius: "12px",
            marginBottom: "1rem",
          }}
        />

        {/* Scale-down */}
        <h3>5. Scale-down (smaller of none or contain)</h3>
        <CodeCopyBlock
          code={`img { width: 300px; height: 150px; object-fit: scale-down; }`}
        />
        <img
          src="https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=1200&q=80"
          alt="scale-down"
          style={{
            width: "300px",
            height: "150px",
            objectFit: "scale-down",
            borderRadius: "12px",
            marginBottom: "1rem",
          }}
        />
      </div>

      {/* Object Position */}
      <div className="tutorial-section">
        <h2 className="section-title">🎯 Object Position</h2>
        <p>Control the alignment inside the container:</p>
        <CodeCopyBlock
          code={`img { width: 300px; height: 150px; object-fit: cover; object-position: top right; }`}
        />
        <img
          src="https://images.unsplash.com/photo-1518791841217-8f162f1e1131?auto=format&fit=crop&w=600&q=80"
          alt="object-position"
          style={{
            width: "300px",
            height: "150px",
            objectFit: "cover",
            objectPosition: "top right",
            borderRadius: "12px",
            marginBottom: "1rem",
          }}
        />
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef3c7, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Combine <code>object-fit</code> with responsive design for adaptive
            images.
          </li>
          <li>
            Use <code>object-position</code> to control cropping in
            cover/contain images.
          </li>
          <li>
            Animate <code>object-position</code> or <code>object-fit</code> on
            hover for interactive effects.
          </li>
          <li>
            Use <code>object-fit</code> on videos or background elements for
            dynamic layouts.
          </li>
          <li>
            Combine with filters and transforms for advanced image effects.
          </li>
        </ul>
      </div>
    </div>
  );
}
